import { css } from '@emotion/react';
import { darken, lighten, mix, rgba } from 'polished';
import { colorYiq } from './bootstrap/scss/_functions';

/**
 * Full ButtonVariant — replicates buttons.mixin.js:
 * colorYiq for text contrast, lift/shadow hover, focus ring, pressed active.
 */
function btnVariant($, bg) {
  const border = darken(0.04, bg);
  const hoverBg = lighten(0.05, bg);
  const hoverBorder = lighten(0.04, bg);
  const activeBg = darken(0.06, bg);
  const activeBorder = darken(0.08, bg);
  const focusWidth = $.btnFocusWidth || '0.2rem';
  const black = $.black || '#000';
  const gray200 = $.gray200 || '#eeeeee';
  const restShadow = `0 1px 2px 0 ${rgba(black, 0.08)}, 0 1px 1px 0 ${rgba(black, 0.04)}`;
  const focusRing = `0 0 0 ${focusWidth} ${rgba(bg, 0.3)}`;

  return css`
    color: ${colorYiq($, bg)};
    background-color: ${bg};
    border-color: ${border};
    box-shadow: ${restShadow};
    transition: background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, transform 100ms ease;

    .text-center > .form-check {
      text-align: center;
    }

    &:hover {
      color: ${colorYiq($, hoverBg)};
      background-color: ${hoverBg};
      border-color: ${hoverBorder};
      box-shadow: 0 2px 4px 0 ${rgba(black, 0.12)}, 0 1px 2px 0 ${rgba(black, 0.06)};
      transform: translateY(-1px);
    }

    &:focus,
    &.focus {
      color: ${colorYiq($, hoverBg)};
      background-color: ${hoverBg};
      border-color: ${hoverBorder};
      outline: none;
      box-shadow: ${focusRing};
      transform: translateY(-1px);
    }

    &.disabled,
    &:disabled {
      color: ${rgba(colorYiq($, bg), 0.5)};
      background-color: ${mix(0.5, bg, gray200)};
      border-color: ${mix(0.5, bg, gray200)};
      box-shadow: none;
      transform: none;
      cursor: not-allowed;
      opacity: 0.85;
    }

    &:not(:disabled):not(.disabled):active,
    &:not(:disabled):not(.disabled).active,
    .show > &.dropdown-toggle {
      color: ${colorYiq($, activeBg)};
      background-color: ${activeBg};
      border-color: ${activeBorder};
      box-shadow: inset 0 1px 2px 0 ${rgba(black, 0.15)};
      transform: translateY(0);
      transition-duration: 50ms;

      &:focus {
        box-shadow: inset 0 1px 2px 0 ${rgba(black, 0.15)}, ${focusRing};
      }
    }
  `;
}

/**
 * ButtonOutlineVariant — replicates buttons.mixin.js:
 * colorYiq for hover fill, subtle tint on hover, fill on active.
 */
function btnOutlineVariant($, color) {
  const colorHover = colorYiq($, color);
  const activeBg = darken(0.06, color);
  const focusWidth = $.btnFocusWidth || '0.2rem';
  const black = $.black || '#000';
  const focusRing = `0 0 0 ${focusWidth} ${rgba(color, 0.25)}`;

  return css`
    color: ${color};
    background-color: transparent;
    border-color: ${color};
    box-shadow: none;
    transition: background-color 150ms ease, color 150ms ease, border-color 150ms ease, box-shadow 150ms ease, transform 100ms ease;

    &:hover {
      color: ${colorHover};
      background-color: ${color};
      border-color: ${color};
      transform: translateY(-1px);
      box-shadow: 0 2px 4px 0 ${rgba(color, 0.2)};
    }

    &:focus,
    &.focus {
      outline: none;
      box-shadow: ${focusRing};
    }

    &.disabled,
    &:disabled {
      color: ${rgba(color, 0.45)};
      border-color: ${rgba(color, 0.3)};
      background-color: transparent;
      box-shadow: none;
      transform: none;
      cursor: not-allowed;
      opacity: 0.65;
    }

    &:not(:disabled):not(.disabled):active,
    &:not(:disabled):not(.disabled).active,
    .show > &.dropdown-toggle {
      color: ${colorYiq($, activeBg)};
      background-color: ${activeBg};
      border-color: ${activeBg};
      box-shadow: inset 0 1px 2px 0 ${rgba(black, 0.12)};
      transform: translateY(0);
      transition-duration: 50ms;

      &:focus {
        box-shadow: inset 0 1px 2px 0 ${rgba(black, 0.12)}, ${focusRing};
      }
    }
  `;
}

/**
 * Bootstrap-compatible component base styles.
 * Replaces the CSS that Bootstrap's emotion CSS-in-JS previously generated.
 * Lives here (non-layered) so it has guaranteed higher cascade priority
 * than Tailwind's @layer components in tailwind.css.
 */
export default $ => css`
  /* ── Buttons ──────────────────────────────────────────────────────────── */
  button {
    cursor: pointer !important;
  }

  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.375rem 0.75rem;
    font-size: 0.9rem;
    font-weight: 400;
    line-height: 1.5;
    text-align: center;
    text-decoration: none;
    white-space: nowrap;
    vertical-align: middle;
    cursor: pointer;
    user-select: none;
    background-color: transparent;
    border: 1px solid transparent;
    border-radius: ${$.borderRadius};
    transition: color 0.15s ease-in-out, background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
  }
  .btn:focus {
    outline: 0;
  }
  .btn.disabled,
  .btn:disabled {
    opacity: 0.65;
    pointer-events: none;
  }
  .btn-xs {
    height: 26px !important;
    padding: 0.125rem 0.35rem;
    font-size: 0.7rem;
    border-radius: ${$.borderRadiusLg};
  }
  .btn-sm {
    height: 30px !important;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    border-radius: ${$.borderRadiusLg};
  }
  .btn-lg {
    padding: 0.5rem 1rem;
    font-size: 1rem;
    border-radius: ${$.borderRadiusLg};
  }
  .btn-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1em;
    height: 1em;
    padding: 0.25em;
    background: transparent
      url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23000'%3e%3cpath d='M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z'/%3e%3c/svg%3e")
      center/1em no-repeat;
    border: 0;
    border-radius: ${$.borderRadius};
    opacity: 0.5;
    cursor: pointer;
  }
  .btn-close:hover {
    opacity: 0.75;
  }
  a.btn {
    &:hover,
    &:active,
    &:focus {
      text-decoration: none !important;
    }
  }

  /* ── Solid variants — colorYiq text + lift/shadow hover from ButtonVariant mixin ── */
  .btn-primary {
    ${btnVariant($, $.primaryMain)}
  }
  .btn-secondary {
    ${btnVariant($, $.secondaryMain)}
  }
  .btn-success {
    ${btnVariant($, $.successMain)}
  }
  .btn-danger {
    ${btnVariant($, $.dangerMain)}
  }
  .btn-warning {
    ${btnVariant($, $.warningMain)}
  }
  .btn-info {
    ${btnVariant($, $.infoMain)}
  }
  .btn-light {
    ${btnVariant($, $.gray100 || '#f5f5f5')}
  }
  .btn-dark {
    ${btnVariant($, $.gray900 || '#212121')}
  }
  .btn-link {
    color: ${$.primaryMain};
    text-decoration: underline;
    background-color: transparent;
    border-color: transparent;
    box-shadow: none;
    &:hover {
      color: ${darken(0.2, $.primaryMain)};
      text-decoration: underline;
    }
    &:focus {
      box-shadow: none;
    }
    &.disabled,
    &:disabled {
      color: ${$.gray600 || '#757575'};
      text-decoration: none;
    }
  }

  /* ── Outline variants — colorYiq on active fill from ButtonOutlineVariant mixin ── */
  .btn-outline-primary {
    ${btnOutlineVariant($, $.primaryMain)}
  }
  .btn-outline-secondary {
    ${btnOutlineVariant($, $.secondaryMain)}
  }
  .btn-outline-success {
    ${btnOutlineVariant($, $.successMain)}
  }
  .btn-outline-danger {
    ${btnOutlineVariant($, $.dangerMain)}
  }
  .btn-outline-warning {
    ${btnOutlineVariant($, $.warningMain)}
  }
  .btn-outline-info {
    ${btnOutlineVariant($, $.infoMain)}
  }
  .btn-outline-light {
    ${btnOutlineVariant($, $.gray300 || '#e0e0e0')}
  }
  .btn-outline-dark {
    ${btnOutlineVariant($, $.gray900 || '#212121')}
  }

  .btn-group {
    display: inline-flex;
  }
  .btn-group > .btn:not(:first-of-type) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  .btn-group > .btn:not(:last-child) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .btn-group-sm > .btn {
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
  }
  .btn-group-vertical {
    display: inline-flex;
    flex-direction: column;
  }

  /* ── Cards ────────────────────────────────────────────────────────────── */
  .card {
    position: relative;
    display: flex;
    flex-direction: column;
    min-width: 0;
    background-color: ${$.white};
    border: 1px solid ${$.gray300};
    border-radius: ${$.borderRadius};
  }
  .card-body {
    flex: 1 1 auto;
    padding: 1rem;
  }
  .card-header {
    padding: 0.5rem 1rem;
    background-color: ${$.gray100};
    border-bottom: 1px solid ${$.gray300};
  }
  .card-footer {
    padding: 0.5rem 1rem;
    background-color: ${$.gray100};
    border-top: 1px solid ${$.gray300};
  }
  .card-title {
    margin-bottom: 0.5rem;
    font-weight: 500;
  }

  /* ── Modal ────────────────────────────────────────────────────────────── */
  .modal-backdrop {
    position: fixed;
    inset: 0;
    background-color: ${$.black};
    opacity: 0.5;
    transition: opacity 200ms ease-out;

    /* Headless UI sets data-closed during the leave transition */
    &[data-closed] {
      opacity: 0;
    }
  }
  .modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    overflow-x: hidden;
    overflow-y: auto;
    outline: 0;
  }
  .modal-dialog {
    position: relative;
    width: auto;
    margin: 1.75rem auto;
    max-width: 500px;
    pointer-events: none;
    transition: opacity 200ms ease-out, transform 200ms ease-out;
    transform-origin: center top;

    /* Scale down + fade out on leave; Headless UI adds data-closed during transition */
    &[data-closed] {
      opacity: 0;
      transform: scale(0.96) translateY(-8px);
    }
  }
  .modal-dialog-centered {
    display: flex;
    align-items: center;
    min-height: calc(100% - 3.5rem);
  }
  .modal-sm {
    max-width: 300px;
  }
  .modal-lg {
    max-width: 800px;
  }
  .modal-xl {
    max-width: 1140px;
  }
  .modal-content {
    position: relative;
    display: flex;
    flex-direction: column;
    width: 100%;
    pointer-events: auto;
    background-color: ${$.white};
    border: 1px solid rgba(0, 0, 0, 0.2);
    border-radius: ${$.borderRadiusLg};
    outline: 0;
  }
  .modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border-bottom: 1px solid ${$.gray300};
  }
  .modal-body {
    flex: 1 1 auto;
    padding: 1rem;
  }
  .modal-footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.75rem;
    border-top: 1px solid ${$.gray300};
  }
  .modal-content-floating {
    overflow: hidden;

    .modal-body {
      overflow: auto;
    }
  }
  .modal-nonmodal {
    pointer-events: none;

    .modal-content {
      pointer-events: auto;
    }
  }
  .modal-resize-grip {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 16px;
    height: 16px;
    cursor: nwse-resize;

    &::after {
      content: '';
      display: block;
      width: 8px;
      height: 8px;
      border-right: 2px solid ${$.gray400};
      border-bottom: 2px solid ${$.gray400};
      position: absolute;
      bottom: 4px;
      right: 4px;
    }
  }

  /* ── Forms ────────────────────────────────────────────────────────────── */
  .form-control,
  .form-select {
    display: block;
    width: 100%;
    padding: 0.375rem 0.75rem;
    // font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: ${$.gray900};
    background-color: ${$.white};
    border: 1px solid ${$.gray400};
    border-radius: ${$.borderRadius};
    appearance: none;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .form-control:focus,
  .form-select:focus {
    border-color: ${$.primaryMain};
    box-shadow: 0 0 0 0.25rem rgba(0, 0, 0, 0.1);
    outline: 0;
  }
  .form-control::placeholder,
  input::placeholder,
  textarea::placeholder {
    color: ${$.gray400};
    opacity: 1;
  }
  .form-control-sm {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    border-radius: ${$.borderRadiusSm};
  }
  .form-control-lg {
    padding: 0.5rem 1rem;
    font-size: 1.25rem;
    border-radius: ${$.borderRadiusLg};
  }
  .form-select {
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right 0.75rem center;
    background-size: 16px 12px;
    padding-right: 2.25rem;
  }
  .form-control-plaintext {
    display: block;
    width: 100%;
    padding: 0.2rem 0;
    background-color: transparent;
    border: solid transparent;
    border-width: 1px 0;
  }
  .form-check {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 1.5rem;
    padding-left: 1.5em;
  }
  .form-check-left {
    .form-check {
      justify-content: left !important;
    }
  }
  .form-check-input {
    width: 1em;
    height: 1em;
    margin-top: 0.25em;
    vertical-align: top;
    float: left;
    margin-left: -1.5em;
    border: 1px solid ${$.gray400};
    border-radius: 0.25em;
  }
  .form-check-input:checked {
    background-color: ${$.primaryMain};
    border-color: ${$.primaryMain};
  }
  .form-check-label {
    cursor: pointer;
  }
  .form-text {
    font-size: 0.875em;
    color: ${$.gray600};
    margin-top: 0.25rem;
  }

  .form-control.is-invalid,
  .is-invalid .form-control {
    border-color: ${$.dangerMain};
  }
  .form-control.is-valid,
  .is-valid .form-control {
    border-color: ${$.successMain};
  }
  .invalid-feedback {
    display: none;
    width: 100%;
    margin-top: 0.25rem;
    font-size: 0.875em;
    color: ${$.dangerMain};
  }
  .valid-feedback {
    display: none;
    width: 100%;
    margin-top: 0.25rem;
    font-size: 0.875em;
    color: ${$.successMain};
  }
  .form-control.is-invalid ~ .invalid-feedback,
  .was-validated .form-control:invalid ~ .invalid-feedback {
    display: block;
  }
  .form-control.is-valid ~ .valid-feedback,
  .was-validated .form-control:valid ~ .valid-feedback {
    display: block;
  }

  .input-group {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    width: 100%;
  }
  .input-group > .form-control {
    position: relative;
    flex: 1 1 auto;
    width: 1%;
    min-width: 0;
  }
  .input-group:not(.has-validation) > :not(:last-child):not(.dropdown-toggle) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .input-group > :not(:first-of-type):not(.dropdown-menu) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  .input-group-text,
  .date-picker-mode-select {
    display: flex;
    color: ${$.gray600};
    align-items: center;
    justify-content: center;
    padding: 0.375rem 0.75rem;
    font-weight: 400;
    line-height: 1.5;
    text-align: center;
    white-space: nowrap;
    background-color: ${$.gray100};
    border: 1px solid ${$.gray400};
    border-radius: ${$.borderRadius};
  }
  .date-picker-mode-select {
    background: ${$.gray100}
      url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")
      right 0.75rem center/16px 12px no-repeat !important;
  }
  .input-group-sm > .form-control,
  .input-group-sm > .input-group-text {
    padding: 0.25rem 0.5rem;
    font-size: 0.875rem;
    border-radius: ${$.borderRadiusSm};
  }
  /* Bootstrap 4 compat */
  .input-group-append,
  .input-group-prepend {
    display: flex;
  }
  .input-group-append > .btn,
  .input-group-prepend > .btn {
    position: relative;
    z-index: 2;
  }

  .input-group-prepend + .input-group-prepend,
  .input-group-prepend + .form-control {
    border-left-width: 0;
  }

  .input-group-prepend + .input-group-prepend,
  .input-group-prepend + .form-control,
  .form-control + .input-group-append,
  .input-group-prepend + .react-datepicker__wrapper {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .input-group-prepend + .react-datepicker-wrapper input.form-control {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left-width: 0;
  }

  .input-group-prepend + .form-check:has(label.checkbox) {
    cursor: pointer;
    border: 1px solid ${$.gray400};
    border-left-width: 0;
    flex-grow: 1;
    padding-left: 30px;
    border-top-right-radius: ${$.borderRadius};
    border-bottom-right-radius: ${$.borderRadius};

    label.checkbox {
      margin-bottom: 0;
      cursor: pointer;
    }

    .form-check-input {
      margin-top: 0;
    }
  }

  .form-control:has(+ .input-group-append),
  .input-group-append:has(+ .input-group-append),
  .react-datepicker__input-container:has(+ .input-group-append) {
    border-right-width: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  .react-datepicker-wrapper:has(+ .input-group-append) {
    & input.form-control {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  /* ── Alerts ───────────────────────────────────────────────────────────── */
  .alert {
    position: relative;
    padding: 1rem;
    margin-bottom: 1rem;
    border: 1px solid transparent;
    border-radius: ${$.borderRadius};
  }
  .alert-dismissible {
    padding-right: 3rem;
  }
  .alert-dismissible .btn-close {
    position: absolute;
    top: 0;
    right: 0;
    z-index: 2;
    padding: 1.25rem 1rem;
  }

  .alert-primary {
    color: ${darken('0.1', $.primaryMain)};
    background-color: ${mix(0.12, $.primaryMain, '#ffffff')};
    border-color: ${mix(0.3, $.primaryMain, '#ffffff')};
  }
  .alert-secondary {
    color: ${darken('0.1', $.secondaryMain)};
    background-color: ${mix(0.12, $.secondaryMain, '#ffffff')};
    border-color: ${mix(0.3, $.secondaryMain, '#ffffff')};
  }
  .alert-success {
    color: ${darken('0.1', $.successMain)};
    background-color: ${mix(0.12, $.successMain, '#ffffff')};
    border-color: ${mix(0.3, $.successMain, '#ffffff')};
  }
  .alert-danger {
    color: ${darken('0.1', $.dangerMain)};
    background-color: ${mix(0.12, $.dangerMain, '#ffffff')};
    border-color: ${mix(0.3, $.dangerMain, '#ffffff')};
  }
  .alert-warning {
    color: ${darken('0.2', $.warningMain)};
    background-color: ${mix(0.15, $.warningMain, '#ffffff')};
    border-color: ${mix(0.35, $.warningMain, '#ffffff')};
  }
  .alert-info {
    color: ${darken('0.1', $.infoMain)};
    background-color: ${mix(0.2, $.infoMain, '#ffffff')};
    border-color: ${mix(0.3, $.infoMain, '#ffffff')};
  }
  .alert-light {
    color: ${$.gray700};
    background-color: ${$.gray100};
    border-color: ${$.gray200};
  }
  .alert-dark {
    color: ${$.gray100};
    background-color: ${$.gray800};
    border-color: ${$.gray700};
  }

  /* ── Badge ────────────────────────────────────────────────────────────── */
  .badge {
    display: inline-flex;
    padding: 0.35em 0.65em;
    font-size: 0.75em;
    font-weight: 700;
    line-height: 1;
    color: ${$.white};
    text-align: center;
    white-space: nowrap;
    vertical-align: baseline;
    border-radius: ${$.borderRadius};
  }
  /* colorYiq (BadgeVariant) — text auto-contrasts against background */
  .bg-primary {
    background-color: ${$.primaryMain} !important;
    color: ${colorYiq($, $.primaryMain)} !important;
  }
  .bg-secondary {
    background-color: ${$.secondaryMain} !important;
    color: ${colorYiq($, $.secondaryMain)} !important;
  }
  .bg-success {
    background-color: ${$.successMain} !important;
    color: ${colorYiq($, $.successMain)} !important;
  }
  .bg-danger {
    background-color: ${$.dangerMain} !important;
    color: ${colorYiq($, $.dangerMain)} !important;
  }
  .bg-warning {
    background-color: ${$.warningMain} !important;
    color: ${colorYiq($, $.warningMain)} !important;
  }
  .bg-info {
    background-color: ${$.infoMain} !important;
    color: ${colorYiq($, $.infoMain)} !important;
  }
  .bg-light {
    background-color: ${$.gray100 || '#f5f5f5'} !important;
    color: ${colorYiq($, $.gray100 || '#f5f5f5')} !important;
  }
  .bg-dark {
    background-color: ${$.gray900 || '#212121'} !important;
    color: ${colorYiq($, $.gray900 || '#212121')} !important;
  }
  .bg-white {
    background-color: ${$.white || '#fff'};
    color: ${colorYiq($, $.white || '#fff')} !important;
  }

  /* Bootstrap 4 .badge-{color} aliases — same colorYiq logic as .bg-* */
  .badge-primary {
    background-color: ${$.primaryMain};
    color: ${colorYiq($, $.primaryMain)};
  }
  .badge-secondary {
    background-color: ${$.secondaryMain};
    color: ${colorYiq($, $.secondaryMain)};
  }
  .badge-success {
    background-color: ${$.successMain};
    color: ${colorYiq($, $.successMain)};
  }
  .badge-danger,
  .badge-error {
    background-color: ${$.dangerMain};
    color: ${colorYiq($, $.dangerMain)};
  }
  .badge-warning {
    background-color: ${$.warningMain};
    color: ${colorYiq($, $.warningMain)};
  }
  .badge-info {
    background-color: ${$.infoMain};
    color: ${colorYiq($, $.infoMain)};
  }
  .badge-light {
    background-color: ${$.gray100 || '#f5f5f5'};
    color: ${colorYiq($, $.gray100 || '#f5f5f5')};
  }
  .badge-dark {
    background-color: ${$.gray900 || '#212121'};
    color: ${colorYiq($, $.gray900 || '#212121')};
  }
  .badge-white {
    background-color: ${$.white || '#fff'};
    color: ${colorYiq($, $.white || '#fff')};
  }

  /* ── Spinner ──────────────────────────────────────────────────────────── */
  .spinner-border {
    display: inline-block;
    width: 2rem;
    height: 2rem;
    vertical-align: -0.125em;
    border: 0.25em solid currentcolor;
    border-right-color: transparent;
    border-radius: 50%;
    animation: _spinner-border 0.75s linear infinite;
  }
  .spinner-border-sm {
    width: 1rem;
    height: 1rem;
    border-width: 0.2em;
  }
  .spinner-grow {
    display: inline-block;
    width: 2rem;
    height: 2rem;
    vertical-align: -0.125em;
    background-color: currentcolor;
    border-radius: 50%;
    opacity: 0;
    animation: _spinner-grow 0.75s linear infinite;
  }
  .spinner-grow-sm {
    width: 1rem;
    height: 1rem;
  }
  @keyframes _spinner-border {
    to {
      transform: rotate(360deg);
    }
  }
  @keyframes _spinner-grow {
    0% {
      transform: scale(0);
    }
    50% {
      opacity: 1;
      transform: none;
    }
  }

  /* ── Tables ───────────────────────────────────────────────────────────── */
  .table {
    width: 100%;
    margin-bottom: 1rem;
    color: ${$.gray900};
    vertical-align: top;
  }
  .table,
  .th,
  .td,
  .tr {
    border-color: ${$.gray300} !important;
  }
  .table > :not(caption) > * > * {
    padding: 0.5rem;
    border-color: ${$.gray300};
  }
  .table > tbody {
    vertical-align: inherit;
  }
  .table > thead {
    vertical-align: bottom;
  }
  .table-bordered {
    border: 1px solid ${$.gray300};
  }
  .table-borderless > :not(caption) > * > * {
    border-bottom-width: 0;
  }
  .table-bordered > :not(caption) > * {
    border-width: 1px 0;
  }
  .table-bordered > :not(caption) > * > * {
    border-width: 0 1px;
  }
  .table-striped > tbody > tr:nth-of-type(odd) > * {
    background-color: rgba(0, 0, 0, 0.05);
  }
  .table-hover > tbody > tr:hover > * {
    background-color: rgba(0, 0, 0, 0.075);
  }
  .table-sm > :not(caption) > * > * {
    padding: 0.25rem;
  }
  .table-responsive {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  /* ── Nav ──────────────────────────────────────────────────────────────── */
  .nav {
    display: flex;
    flex-direction: row !important;
    flex-wrap: wrap;
    padding-left: 0;
    margin-bottom: 0;
    list-style: none;
  }
  .nav-link {
    display: block;
    padding: 0.5rem 1rem;
    color: ${$.primaryMain};
    text-decoration: none;
    transition: color 0.15s;
  }
  .nav-link:hover {
    color: ${darken('0.15', $.primaryMain)};
  }
  .nav-link.disabled {
    color: ${$.gray600};
    pointer-events: none;
    cursor: default;
  }
  .nav-tabs {
    border-bottom: 1px solid ${$.gray300};
  }
  .nav-tabs .nav-link {
    border: 1px solid transparent;
    border-top-left-radius: ${$.borderRadius};
    border-top-right-radius: ${$.borderRadius};
    margin-bottom: -1px;
  }
  .nav-tabs .nav-link.active,
  .nav-tabs .nav-item.show .nav-link {
    color: #495057;
    background-color: ${$.white};
    border-color: ${$.gray300} ${$.gray300} ${$.white};
  }
  .nav-pills .nav-link {
    border-radius: ${$.borderRadius};
  }
  .nav-pills .nav-link.active {
    background-color: ${$.primaryMain};
    color: ${$.white};
  }

  /* ── Navbar ───────────────────────────────────────────────────────────── */
  .navbar {
    position: relative;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem;
  }
  .navbar-brand {
    display: inline-block;
    padding-top: 0.3125rem;
    padding-bottom: 0.3125rem;
    margin-right: 1rem;
    font-size: 1.25rem;
    white-space: nowrap;
  }
  .navbar-nav {
    display: flex;
    padding-left: 0;
    margin-bottom: 0;
    list-style: none;
  }
  .navbar-nav .nav-link {
    padding-right: 0;
    padding-left: 0;
  }
  .navbar-text {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
  .navbar-collapse {
    flex-basis: 100%;
    flex-grow: 1;
    align-items: center;
  }
  .navbar-toggler {
    padding: 0.25rem 0.75rem;
    font-size: 1.25rem;
    line-height: 1;
    background-color: transparent;
    border: 1px solid transparent;
    border-radius: ${$.borderRadius};
    cursor: pointer;
  }
  .navbar-expand {
    flex-wrap: nowrap;
    justify-content: flex-start;
  }
  .navbar-expand .navbar-nav {
    flex-direction: row;
  }
  .navbar-expand .navbar-nav .nav-link {
    padding-right: 0.5rem;
    padding-left: 0.5rem;
  }
  .navbar-expand .navbar-collapse {
    display: flex !important;
    flex-basis: auto;
  }

  @media (min-width: 576px) {
    .navbar-expand-sm {
      flex-wrap: nowrap;
      justify-content: flex-start;
    }
    .navbar-expand-sm .navbar-nav {
      flex-direction: row;
    }
    .navbar-expand-sm .navbar-nav .nav-link {
      padding-right: 0.5rem;
      padding-left: 0.5rem;
    }
    .navbar-expand-sm .navbar-collapse {
      display: flex !important;
      flex-basis: auto;
    }
  }
  @media (min-width: 768px) {
    .navbar-expand-md {
      flex-wrap: nowrap;
      justify-content: flex-start;
    }
    .navbar-expand-md .navbar-nav {
      flex-direction: row;
    }
    .navbar-expand-md .navbar-collapse {
      display: flex !important;
      flex-basis: auto;
    }
  }
  @media (min-width: 992px) {
    .navbar-expand-lg {
      flex-wrap: nowrap;
      justify-content: flex-start;
    }
    .navbar-expand-lg .navbar-nav {
      flex-direction: row;
    }
    .navbar-expand-lg .navbar-collapse {
      display: flex !important;
      flex-basis: auto;
    }
  }
  @media (min-width: 1200px) {
    .navbar-expand-xl {
      flex-wrap: nowrap;
      justify-content: flex-start;
    }
    .navbar-expand-xl .navbar-nav {
      flex-direction: row;
    }
    .navbar-expand-xl .navbar-collapse {
      display: flex !important;
      flex-basis: auto;
    }
  }

  /* ── Dropdown ─────────────────────────────────────────────────────────── */
  .dropdown {
    position: relative;
  }
  .dropdown-toggle::after {
    display: inline-block;
    margin-left: 0.255em;
    vertical-align: 0.255em;
    content: '';
    border-top: 0.3em solid;
    border-right: 0.3em solid transparent;
    border-bottom: 0;
    border-left: 0.3em solid transparent;
  }
  .dropdown-menu {
    position: absolute;
    z-index: 1000;
    display: none;
    min-width: 10rem;
    padding: 0.5rem 0;
    margin: 0;
    font-size: 1rem;
    color: ${$.gray900};
    text-align: left;
    list-style: none;
    background-color: ${$.white};
    background-clip: padding-box;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: ${$.borderRadius};
  }
  .dropdown-menu.show {
    display: block;
  }
  .dropdown-menu-end {
    right: 0;
    left: auto;
  }
  .dropdown-item {
    width: 100%;
    padding: 0.25rem 1rem;
    clear: both;
    font-weight: 400;
    color: ${$.gray900};
    text-align: inherit;
    white-space: nowrap;
    background-color: transparent;
    border: 0;
    cursor: pointer;
    text-decoration: none;
  }
  .dropdown-item:hover,
  .dropdown-item:focus {
    background-color: ${$.gray100};
  }
  .dropdown-item.active {
    background-color: ${$.primaryMain};
    color: ${$.white};
  }
  .dropdown-item.disabled {
    color: ${$.gray600};
    pointer-events: none;
    background-color: transparent;
  }
  .dropdown-divider {
    height: 0;
    margin: 0.5rem 0;
    overflow: hidden;
    border-top: 1px solid rgba(0, 0, 0, 0.15);
  }
  .dropdown-header {
    display: block;
    padding: 0.5rem 1rem;
    margin-bottom: 0;
    font-size: 0.875rem;
    color: ${$.gray600};
    white-space: nowrap;
  }

  /* ── List group ───────────────────────────────────────────────────────── */
  .list-group {
    display: flex;
    flex-direction: column;
    padding-left: 0;
    margin-bottom: 0;
    border-radius: ${$.borderRadius};
  }
  .list-group-item {
    position: relative;
    display: block;
    padding: 0.5rem 1rem;
    color: ${$.gray900};
    text-decoration: none;
    background-color: ${$.white};
    border: 1px solid rgba(0, 0, 0, 0.125);
  }
  .list-group-item:first-of-type {
    border-top-left-radius: inherit;
    border-top-right-radius: inherit;
  }
  .list-group-item:last-child {
    border-bottom-right-radius: inherit;
    border-bottom-left-radius: inherit;
  }
  .list-group-item.active {
    z-index: 2;
    color: ${$.white};
    background-color: ${$.primaryMain};
    border-color: ${$.primaryMain};
  }
  .list-group-item.disabled {
    color: ${$.gray600};
    pointer-events: none;
    background-color: ${$.white};
  }
  .list-group-item-action {
    width: 100%;
    color: #495057;
    cursor: pointer;
  }
  .list-group-item-action:hover {
    background-color: ${$.gray100};
  }
  .list-group-flush {
    border-radius: 0;
  }
  .list-group-flush > .list-group-item {
    border-width: 0 0 1px;
  }

  /* ── Collapse ─────────────────────────────────────────────────────────── */
  /* Tailwind v4 generates .collapse { visibility: collapse } as a utility.
     Override with visibility: visible so Bootstrap's collapse pattern works. */
  .collapse {
    visibility: visible;
  }
  .collapse:not(.show) {
    display: none;
  }
  .collapse.show {
    display: block;
    visibility: visible;
  }
  .collapsing {
    position: relative;
    height: 0;
    overflow: hidden;
    transition: height 0.35s ease;
  }
  .navbar-collapse.show,
  .navbar-collapse.collapsing {
    display: flex;
  }

  /* ── Tabs ─────────────────────────────────────────────────────────────── */
  .tab-content > .tab-pane {
    display: none;
  }
  .tab-content > .active {
    display: block;
  }
  .fade {
    transition: opacity 0.15s linear;
  }
  .fade:not(.show) {
    opacity: 0;
  }

  /* ── Tooltip ──────────────────────────────────────────────────────────── */
  .tooltip {
    position: absolute;
    z-index: 1080;
    display: block;
    font-size: 0.875rem;
    word-wrap: break-word;
    opacity: 1;
  }
  .tooltip.show {
    opacity: 1;
  }
  .tooltip-inner {
    /* Shared base — no max-height so rich content is never clipped.
       max-width caps at 500px but never overflows the viewport. */
    max-width: min(500px, calc(100vw - 16px));
    padding: 0.35rem 0.6rem;
    border-radius: ${$.borderRadius};
    line-height: 1.4;
  }
  /* ── Light variant (default) ─────────────────────────────────────────── */
  .tooltip-light .tooltip-inner {
    color: ${$.gray900};
    text-align: left;
    background-color: ${$.white};
    border: 1px solid ${$.gray300};
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
  }
  /* ── Dark variant ────────────────────────────────────────────────────── */
  .tooltip-dark .tooltip-inner {
    color: ${$.white};
    text-align: center;
    background-color: rgba(33, 37, 41, 0.92);
    border: none;
    box-shadow: none;
  }
  .tooltip-arrow {
    position: absolute;
    display: block;
    width: 0.8rem;
    height: 0.4rem;
  }

  /* ── Grid ─────────────────────────────────────────────────────────────── */
  .container,
  .container-fluid {
    width: 100%;
    // padding-right: 0.75rem;
    // padding-left: 0.75rem;
    margin-right: auto;
    margin-left: auto;
  }
  @media (min-width: 576px) {
    .container {
      max-width: 540px;
    }
  }
  @media (min-width: 768px) {
    .container {
      max-width: 720px;
    }
  }
  @media (min-width: 992px) {
    .container {
      max-width: 960px;
    }
  }
  @media (min-width: 1200px) {
    .container {
      max-width: 1140px;
    }
  }

  .row {
    --bs-gutter-x: 1.5rem;
    --bs-gutter-y: 0;
    display: flex;
    flex-wrap: wrap;
    margin-top: calc(-1 * var(--bs-gutter-y));
    margin-right: calc(-0.5 * var(--bs-gutter-x));
    margin-left: calc(-0.5 * var(--bs-gutter-x));
  }
  .row > * {
    flex-shrink: 0;
    width: 100%;
    max-width: 100%;
    padding-right: calc(var(--bs-gutter-x) * 0.5);
    padding-left: calc(var(--bs-gutter-x) * 0.5);
    margin-top: var(--bs-gutter-y);
  }
  .g-0 {
    --bs-gutter-x: 0;
    --bs-gutter-y: 0;
  }
  .col {
    flex: 1 0 0%;
  }
  .col-auto {
    flex: 0 0 auto;
    width: auto;
  }
  .col-1 {
    flex: 0 0 auto;
    width: 8.333333%;
  }
  .col-2 {
    flex: 0 0 auto;
    width: 16.666667%;
  }
  .col-3 {
    flex: 0 0 auto;
    width: 25%;
  }
  .col-4 {
    flex: 0 0 auto;
    width: 33.333333%;
  }
  .col-5 {
    flex: 0 0 auto;
    width: 41.666667%;
  }
  .col-6 {
    flex: 0 0 auto;
    width: 50%;
  }
  .col-7 {
    flex: 0 0 auto;
    width: 58.333333%;
  }
  .col-8 {
    flex: 0 0 auto;
    width: 66.666667%;
  }
  .col-9 {
    flex: 0 0 auto;
    width: 75%;
  }
  .col-10 {
    flex: 0 0 auto;
    width: 83.333333%;
  }
  .col-11 {
    flex: 0 0 auto;
    width: 91.666667%;
  }
  .col-12 {
    flex: 0 0 auto;
    width: 100%;
  }

  @media (min-width: 576px) {
    .col-sm {
      flex: 1 0 0%;
    }
    .col-sm-auto {
      flex: 0 0 auto;
      width: auto;
    }
    .col-sm-1 {
      flex: 0 0 auto;
      width: 8.333333%;
    }
    .col-sm-2 {
      flex: 0 0 auto;
      width: 16.666667%;
    }
    .col-sm-3 {
      flex: 0 0 auto;
      width: 25%;
    }
    .col-sm-4 {
      flex: 0 0 auto;
      width: 33.333333%;
    }
    .col-sm-5 {
      flex: 0 0 auto;
      width: 41.666667%;
    }
    .col-sm-6 {
      flex: 0 0 auto;
      width: 50%;
    }
    .col-sm-7 {
      flex: 0 0 auto;
      width: 58.333333%;
    }
    .col-sm-8 {
      flex: 0 0 auto;
      width: 66.666667%;
    }
    .col-sm-9 {
      flex: 0 0 auto;
      width: 75%;
    }
    .col-sm-10 {
      flex: 0 0 auto;
      width: 83.333333%;
    }
    .col-sm-11 {
      flex: 0 0 auto;
      width: 91.666667%;
    }
    .col-sm-12 {
      flex: 0 0 auto;
      width: 100%;
    }
  }
  @media (min-width: 768px) {
    .col-md {
      flex: 1 0 0%;
    }
    .col-md-auto {
      flex: 0 0 auto;
      width: auto;
    }
    .col-md-1 {
      flex: 0 0 auto;
      width: 8.333333%;
    }
    .col-md-2 {
      flex: 0 0 auto;
      width: 16.666667%;
    }
    .col-md-3 {
      flex: 0 0 auto;
      width: 25%;
    }
    .col-md-4 {
      flex: 0 0 auto;
      width: 33.333333%;
    }
    .col-md-5 {
      flex: 0 0 auto;
      width: 41.666667%;
    }
    .col-md-6 {
      flex: 0 0 auto;
      width: 50%;
    }
    .col-md-7 {
      flex: 0 0 auto;
      width: 58.333333%;
    }
    .col-md-8 {
      flex: 0 0 auto;
      width: 66.666667%;
    }
    .col-md-9 {
      flex: 0 0 auto;
      width: 75%;
    }
    .col-md-10 {
      flex: 0 0 auto;
      width: 83.333333%;
    }
    .col-md-11 {
      flex: 0 0 auto;
      width: 91.666667%;
    }
    .col-md-12 {
      flex: 0 0 auto;
      width: 100%;
    }
  }
  @media (min-width: 992px) {
    .col-lg {
      flex: 1 0 0%;
    }
    .col-lg-auto {
      flex: 0 0 auto;
      width: auto;
    }
    .col-lg-1 {
      flex: 0 0 auto;
      width: 8.333333%;
    }
    .col-lg-2 {
      flex: 0 0 auto;
      width: 16.666667%;
    }
    .col-lg-3 {
      flex: 0 0 auto;
      width: 25%;
    }
    .col-lg-4 {
      flex: 0 0 auto;
      width: 33.333333%;
    }
    .col-lg-5 {
      flex: 0 0 auto;
      width: 41.666667%;
    }
    .col-lg-6 {
      flex: 0 0 auto;
      width: 50%;
    }
    .col-lg-7 {
      flex: 0 0 auto;
      width: 58.333333%;
    }
    .col-lg-8 {
      flex: 0 0 auto;
      width: 66.666667%;
    }
    .col-lg-9 {
      flex: 0 0 auto;
      width: 75%;
    }
    .col-lg-10 {
      flex: 0 0 auto;
      width: 83.333333%;
    }
    .col-lg-11 {
      flex: 0 0 auto;
      width: 91.666667%;
    }
    .col-lg-12 {
      flex: 0 0 auto;
      width: 100%;
    }
  }
  @media (min-width: 1200px) {
    .col-xl {
      flex: 1 0 0%;
    }
    .col-xl-auto {
      flex: 0 0 auto;
      width: auto;
    }
    .col-xl-1 {
      flex: 0 0 auto;
      width: 8.333333%;
    }
    .col-xl-2 {
      flex: 0 0 auto;
      width: 16.666667%;
    }
    .col-xl-3 {
      flex: 0 0 auto;
      width: 25%;
    }
    .col-xl-4 {
      flex: 0 0 auto;
      width: 33.333333%;
    }
    .col-xl-5 {
      flex: 0 0 auto;
      width: 41.666667%;
    }
    .col-xl-6 {
      flex: 0 0 auto;
      width: 50%;
    }
    .col-xl-7 {
      flex: 0 0 auto;
      width: 58.333333%;
    }
    .col-xl-8 {
      flex: 0 0 auto;
      width: 66.666667%;
    }
    .col-xl-9 {
      flex: 0 0 auto;
      width: 75%;
    }
    .col-xl-10 {
      flex: 0 0 auto;
      width: 83.333333%;
    }
    .col-xl-11 {
      flex: 0 0 auto;
      width: 91.666667%;
    }
    .col-xl-12 {
      flex: 0 0 auto;
      width: 100%;
    }
  }

  /* ── Misc semantic utilities ──────────────────────────────────────────── */
  .visually-hidden {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }
  .text-muted {
    color: ${$.gray600};
  }
  .text-primary {
    color: ${$.primaryMain};
  }
  .text-danger {
    color: ${$.dangerMain};
  }
  .text-success {
    color: ${$.successMain};
  }
  .text-warning {
    color: ${$.warningMain};
  }
  .text-info {
    color: ${$.infoMain};
  }
  .text-white {
    color: ${$.white};
  }
  .text-gray-100 {
    color: ${$.gray100};
  }
  .text-gray-200 {
    color: ${$.gray200};
  }
  .text-gray-300 {
    color: ${$.gray300};
  }
  .text-gray-400 {
    color: ${$.gray400};
  }
  .text-gray-500 {
    color: ${$.gray500};
  }
  .text-gray-600 {
    color: ${$.gray600};
  }
  .text-dark {
    color: ${$.gray900};
  }
  .text-light {
    color: ${$.gray100};
  }
  .text-bold {
    font-weight: 500;
  }
  .text-bolder {
    font-weight: 600;
  }
  .text-ellipsis {
    width: 100%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .border-primary {
    border-color: ${$.primaryMain} !important;
  }
  .border-danger {
    border-color: ${$.dangerMain} !important;
  }

  .rounded-pill {
    border-radius: 50rem;
  }
  .shadow {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
  }
  .shadow-sm {
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  }
  .shadow-lg {
    box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175);
  }

  /* Bootstrap 4 compat */
  .close {
    opacity: 0.5;
    background: transparent;
    border: 0;
    cursor: pointer;
  }
  .close:hover {
    opacity: 0.75;
  }

  /* ── Base / Reboot (body + links with theme values) ──────────────────── */
  body {
    font-family: ${$.fontFamilyBase};
    font-size: ${$.fontSizeBase};
    font-weight: ${$.fontWeightBase};
    line-height: ${$.lineHeightBase};
    color: ${$.bodyColor};
    background-color: ${$.bodyBg};
  }
  a {
    color: ${$.linkColor};
    text-decoration: ${$.linkDecoration};
  }
  a:hover {
    color: ${$.linkHoverColor};
    text-decoration: ${$.linkHoverDecoration};
  }
  a:not([href]):not([class]),
  a:not([href]):not([class]):hover {
    color: inherit;
    text-decoration: none;
  }
  p {
    margin-top: 0;
    margin-bottom: ${$.paragraphMarginBottom};
  }
  label {
    // display: inline-block;
    margin-bottom: ${$.labelMarginBottom};
  }
  /* Cancel these reboot margins inside the form sticky header so they
     don't inflate --form-sticky-header-height and shift the tab nav down. */
  .form-sticky-header p,
  .form-sticky-header label {
    margin-bottom: 0;
  }
  hr {
    margin-top: ${$.hrMarginY};
    margin-bottom: ${$.hrMarginY};
    border: 0;
    border-top: ${$.hrBorderWidth} solid ${$.hrBorderColor};
  }
  small,
  .small {
    font-size: ${$.smallFontSize};
    font-weight: ${$.fontWeightNormal};
  }
  mark,
  .mark {
    padding: ${$.markPadding};
    background-color: ${$.markBg};
  }

  /* ── Typography ───────────────────────────────────────────────────────── */
  h1,
  h2,
  h3,
  h4,
  h5,
  h6,
  .h1,
  .h2,
  .h3,
  .h4,
  .h5,
  .h6 {
    margin-top: 0;
    margin-bottom: ${$.headingsMarginBottom};
    font-family: ${$.headingsFontFamily};
    font-weight: ${$.headingsFontWeight};
    line-height: ${$.headingsLineHeight};
    color: ${$.headingsColor};
  }
  h1,
  .h1 {
    font-size: ${$.h1FontSize};
  }
  h2,
  .h2 {
    font-size: ${$.h2FontSize};
  }
  h3,
  .h3 {
    font-size: ${$.h3FontSize};
  }
  h4,
  .h4 {
    font-size: ${$.h4FontSize};
  }
  h5,
  .h5 {
    font-size: ${$.h5FontSize};
  }
  h6,
  .h6 {
    font-size: ${$.h6FontSize};
  }

  .lead {
    font-size: ${$.leadFontSize};
    font-weight: ${$.leadFontWeight};
  }

  .display-1 {
    font-size: ${$.display1Size};
    font-weight: ${$.display1Weight};
    line-height: ${$.displayLineHeight};
  }
  .display-2 {
    font-size: ${$.display2Size};
    font-weight: ${$.display2Weight};
    line-height: ${$.displayLineHeight};
  }
  .display-3 {
    font-size: ${$.display3Size};
    font-weight: ${$.display3Weight};
    line-height: ${$.displayLineHeight};
  }
  .display-4 {
    font-size: ${$.display4Size};
    font-weight: ${$.display4Weight};
    line-height: ${$.displayLineHeight};
  }

  .list-unstyled {
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;
  }
  .list-inline {
    list-style: none;
    padding-left: 0;
    margin-bottom: 0;
  }
  .list-inline-item {
    display: inline-block;
  }
  .list-inline-item:not(:last-child) {
    margin-right: ${$.listInlinePadding};
  }
  .initialism {
    font-size: 90%;
    text-transform: uppercase;
  }

  .blockquote {
    margin-bottom: ${$.spacer};
    font-size: ${$.blockquoteFontSize};
  }
  .blockquote-footer {
    display: block;
    font-size: ${$.blockquoteSmallFontSize};
    color: ${$.blockquoteSmallColor};
  }
  .blockquote-footer::before {
    content: '\\2014\\00A0';
  }

  /* ── Images ───────────────────────────────────────────────────────────── */
  .img-fluid {
    max-width: 100%;
    height: auto;
  }
  .img-thumbnail {
    padding: ${$.thumbnailPadding};
    background-color: ${$.thumbnailBg};
    border: ${$.thumbnailBorderWidth} solid ${$.thumbnailBorderColor};
    border-radius: ${$.thumbnailBorderRadius};
    max-width: 100%;
    height: auto;
  }
  .figure {
    display: inline-block;
  }
  .figure-img {
    margin-bottom: 0.5rem;
    line-height: 1;
  }
  .figure-caption {
    font-size: ${$.figureCaptionFontSize};
    color: ${$.figureCaptionColor};
  }

  /* ── Code ─────────────────────────────────────────────────────────────── */
  code {
    font-size: ${$.codeFontSize};
    color: ${$.codeColor};
    word-wrap: break-word;
  }
  kbd {
    padding: ${$.kbdPaddingY} ${$.kbdPaddingX};
    font-size: ${$.kbdFontSize};
    color: ${$.kbdColor};
    background-color: ${$.kbdBg};
    border-radius: ${$.borderRadiusSm};
  }
  pre {
    display: block;
    font-size: ${$.codeFontSize};
    color: ${$.preColor};
    margin-top: 0;
    margin-bottom: 1rem;
    overflow: auto;
  }
  pre code {
    font-size: inherit;
    color: inherit;
    word-break: normal;
  }
  .pre-scrollable {
    max-height: ${$.preScrollableMaxHeight};
    overflow-y: scroll;
  }

  /* ── Progress ─────────────────────────────────────────────────────────── */
  @keyframes progress-bar-stripes {
    from {
      background-position: ${$.progressHeight} 0;
    }
    to {
      background-position: 0 0;
    }
  }
  .progress {
    display: flex;
    height: ${$.progressHeight};
    overflow: hidden;
    font-size: ${$.progressFontSize};
    background-color: ${$.progressBg};
    border-radius: ${$.progressBorderRadius};
  }
  .progress-bar {
    display: flex;
    flex-direction: column;
    justify-content: center;
    overflow: hidden;
    color: ${$.progressBarColor};
    text-align: center;
    white-space: nowrap;
    background-color: ${$.progressBarBg};
    transition: width 0.6s ease;
  }
  .progress-bar-striped {
    background-image: linear-gradient(
      45deg,
      rgba(255, 255, 255, 0.15) 25%,
      transparent 25%,
      transparent 50%,
      rgba(255, 255, 255, 0.15) 50%,
      rgba(255, 255, 255, 0.15) 75%,
      transparent 75%,
      transparent
    );
    background-size: ${$.progressHeight} ${$.progressHeight};
  }
  .progress-bar-animated {
    animation: progress-bar-stripes 1s linear infinite;
  }

  /* ── Breadcrumb ───────────────────────────────────────────────────────── */
  .breadcrumb {
    display: flex;
    flex-wrap: wrap;
    padding: ${$.breadcrumbPaddingY} ${$.breadcrumbPaddingX};
    margin-bottom: ${$.breadcrumbMarginBottom};
    list-style: none;
    background-color: ${$.breadcrumbBg};
    border-radius: ${$.breadcrumbBorderRadius};
  }
  .breadcrumb-item + .breadcrumb-item {
    padding-left: ${$.breadcrumbItemPadding};
  }
  .breadcrumb-item + .breadcrumb-item::before {
    float: left;
    padding-right: ${$.breadcrumbItemPadding};
    color: ${$.breadcrumbDividerColor};
    content: '/';
  }
  .breadcrumb-item.active {
    color: ${$.breadcrumbActiveColor};
  }

  /* ── Pagination ───────────────────────────────────────────────────────── */
  .pagination {
    display: flex;
    list-style: none;
    border-radius: ${$.borderRadius};
    padding: 0;
    margin: 0;
  }
  .page-link {
    position: relative;
    display: block;
    padding: ${$.paginationPaddingY} ${$.paginationPaddingX};
    margin-left: -1px;
    line-height: ${$.paginationLineHeight};
    color: ${$.paginationColor};
    background-color: ${$.paginationBg};
    border: ${$.paginationBorderWidth} solid ${$.paginationBorderColor};
    text-decoration: none;
  }
  .page-link:hover {
    color: ${$.paginationHoverColor};
    background-color: ${$.paginationHoverBg};
    border-color: ${$.paginationHoverBorderColor};
  }
  .page-item:first-of-type .page-link {
    border-top-left-radius: ${$.borderRadius};
    border-bottom-left-radius: ${$.borderRadius};
  }
  .page-item:last-child .page-link {
    border-top-right-radius: ${$.borderRadius};
    border-bottom-right-radius: ${$.borderRadius};
  }
  .page-item.active .page-link {
    z-index: 3;
    color: ${$.white};
    background-color: ${$.primaryMain};
    border-color: ${$.primaryMain};
  }
  .page-item.disabled .page-link {
    color: ${$.paginationDisabledColor};
    pointer-events: none;
    background-color: ${$.paginationDisabledBg};
    border-color: ${$.paginationDisabledBorderColor};
  }
  .pagination-sm .page-link {
    padding: ${$.paginationPaddingYSm} ${$.paginationPaddingXSm};
    font-size: ${$.fontSizeSm};
  }
  .pagination-lg .page-link {
    padding: ${$.paginationPaddingYLg} ${$.paginationPaddingXLg};
    font-size: ${$.fontSizeLg};
  }

  /* ── Custom Forms (Bootstrap 4) ──────────────────────────────────────── */
  textarea {
    field-sizing: content;
    max-height: 350px; /* Stops growing at 200px and displays a scrollbar */
    overflow-y: auto; /* Ensures scrolling triggers correctly */
    resize: none;
  }

  .custom-control {
    position: relative;
    z-index: 1;
    display: block;
    min-height: 1.5rem;
    padding-left: 1.5rem;
  }
  .custom-control-inline {
    display: inline-flex;
    margin-right: 1rem;
  }
  .custom-control-input {
    position: absolute;
    left: 0;
    z-index: -1;
    width: 1rem;
    height: 1.25rem;
    opacity: 0;
  }
  .custom-control-input:checked ~ .custom-control-label::before {
    color: ${$.white};
    border-color: ${$.primaryMain};
    background-color: ${$.primaryMain};
  }
  .custom-control-label {
    position: relative;
    margin-bottom: 0;
    vertical-align: top;
    cursor: pointer;
  }
  .custom-control-label::before {
    position: absolute;
    top: 0.25rem;
    left: -1.5rem;
    display: block;
    width: 1rem;
    height: 1rem;
    pointer-events: none;
    content: '';
    background-color: ${$.white};
    border: ${$.gray500} solid 1px;
    border-radius: ${$.borderRadius};
  }
  .custom-control-label::after {
    position: absolute;
    top: 0.25rem;
    left: -1.5rem;
    display: block;
    width: 1rem;
    height: 1rem;
    content: '';
    background: 50% / 50% 50% no-repeat;
  }
  .custom-checkbox .custom-control-label::before {
    border-radius: ${$.borderRadius};
  }
  .custom-radio .custom-control-label::before {
    border-radius: 50%;
  }
  .custom-switch {
    padding-left: 2.25rem;
  }
  .custom-switch .custom-control-label::before {
    left: -2.25rem;
    width: 1.75rem;
    pointer-events: all;
    border-radius: 0.5rem;
  }
  .custom-switch .custom-control-input:checked ~ .custom-control-label::after {
    background-color: ${$.white};
    transform: translateX(0.75rem);
  }
  .custom-select,
  .form-select {
    display: inline-block;
    width: 100%;
    padding: 0.375rem 1.75rem 0.375rem 0.75rem;
    // font-size: 1rem;
    font-weight: 400;
    line-height: 1.5;
    color: ${$.gray900};
    vertical-align: middle;
    background: ${$.white}
      url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3e%3cpath fill='none' stroke='%23343a40' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='m2 5 6 6 6-6'/%3e%3c/svg%3e")
      right 0.75rem center/16px 12px no-repeat;
    border: 1px solid ${$.gray400};
    border-radius: ${$.borderRadius};
    appearance: none;
  }
  .custom-select:focus,
  .form-select:focus {
    border-color: ${$.primaryMain};
    outline: 0;
  }
  .custom-select:disabled {
    color: ${$.gray600};
    background-color: ${$.gray200};
  }
  .custom-select-sm {
    height: calc(1.5em + 0.5rem + 2px);
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    font-size: 0.875rem;
  }
  .custom-select-lg {
    height: calc(1.5em + 1rem + 2px);
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    font-size: 1.25rem;
  }
  .custom-file {
    position: relative;
    display: inline-block;
    width: 100%;
    height: calc(1.5em + 0.75rem + 2px);
    margin-bottom: 0;
  }
  .custom-file-input {
    position: relative;
    z-index: 2;
    width: 100%;
    height: 100%;
    margin: 0;
    overflow: hidden;
    opacity: 0;
  }
  .custom-file-label {
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    z-index: 1;
    height: 100%;
    padding: 0.375rem 0.75rem;
    font-weight: 400;
    line-height: 1.5;
    color: ${$.gray900};
    background-color: ${$.white};
    border: 1px solid ${$.gray400};
    border-radius: ${$.borderRadius};
  }
  .custom-file-label::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 3;
    display: block;
    padding: 0.375rem 0.75rem;
    line-height: 1.5;
    color: ${$.gray900};
    content: 'Browse';
    background-color: ${$.gray200};
    border-left: inherit;
    border-radius: 0 ${$.borderRadius} ${$.borderRadius} 0;
  }
  .custom-range {
    width: 100%;
    height: 1.4rem;
    padding: 0;
    background-color: transparent;
    appearance: none;
  }
  .custom-range::-webkit-slider-thumb {
    width: 1rem;
    height: 1rem;
    background-color: ${$.primaryMain};
    border: 0;
    border-radius: 1rem;
    appearance: none;
  }
  .custom-range::-webkit-slider-runnable-track {
    width: 100%;
    height: 0.5rem;
    color: transparent;
    background-color: ${$.gray300};
    border-color: transparent;
    border-radius: 1rem;
  }
  .custom-range::-moz-range-thumb {
    width: 1rem;
    height: 1rem;
    background-color: ${$.primaryMain};
    border: 0;
    border-radius: 1rem;
    appearance: none;
  }
  .custom-range::-moz-range-track {
    width: 100%;
    height: 0.5rem;
    background-color: ${$.gray300};
    border-color: transparent;
    border-radius: 1rem;
  }

  /* ── Grid extras: no-gutters, order, offset ──────────────────────────── */
  .no-gutters {
    margin-right: 0;
    margin-left: 0;
  }
  .no-gutters > .col,
  .no-gutters > [class*='col-'] {
    padding-right: 0;
    padding-left: 0;
  }

  /* order (non-responsive) */
  .order-first {
    order: -1;
  }
  .order-last {
    order: 13;
  }
  .order-0 {
    order: 0;
  }
  .order-1 {
    order: 1;
  }
  .order-2 {
    order: 2;
  }
  .order-3 {
    order: 3;
  }
  .order-4 {
    order: 4;
  }
  .order-5 {
    order: 5;
  }
  .order-6 {
    order: 6;
  }
  .order-7 {
    order: 7;
  }
  .order-8 {
    order: 8;
  }
  .order-9 {
    order: 9;
  }
  .order-10 {
    order: 10;
  }
  .order-11 {
    order: 11;
  }
  .order-12 {
    order: 12;
  }

  /* offset (non-responsive) */
  .offset-1 {
    margin-left: 8.333333%;
  }
  .offset-2 {
    margin-left: 16.666667%;
  }
  .offset-3 {
    margin-left: 25%;
  }
  .offset-4 {
    margin-left: 33.333333%;
  }
  .offset-5 {
    margin-left: 41.666667%;
  }
  .offset-6 {
    margin-left: 50%;
  }
  .offset-7 {
    margin-left: 58.333333%;
  }
  .offset-8 {
    margin-left: 66.666667%;
  }
  .offset-9 {
    margin-left: 75%;
  }
  .offset-10 {
    margin-left: 83.333333%;
  }
  .offset-11 {
    margin-left: 91.666667%;
  }

  @media (min-width: 576px) {
    .order-sm-first {
      order: -1;
    }
    .order-sm-last {
      order: 13;
    }
    .order-sm-0 {
      order: 0;
    }
    .order-sm-1 {
      order: 1;
    }
    .order-sm-2 {
      order: 2;
    }
    .order-sm-3 {
      order: 3;
    }
    .order-sm-4 {
      order: 4;
    }
    .order-sm-5 {
      order: 5;
    }
    .order-sm-6 {
      order: 6;
    }
    .order-sm-7 {
      order: 7;
    }
    .order-sm-8 {
      order: 8;
    }
    .order-sm-9 {
      order: 9;
    }
    .order-sm-10 {
      order: 10;
    }
    .order-sm-11 {
      order: 11;
    }
    .order-sm-12 {
      order: 12;
    }
    .offset-sm-0 {
      margin-left: 0;
    }
    .offset-sm-1 {
      margin-left: 8.333333%;
    }
    .offset-sm-2 {
      margin-left: 16.666667%;
    }
    .offset-sm-3 {
      margin-left: 25%;
    }
    .offset-sm-4 {
      margin-left: 33.333333%;
    }
    .offset-sm-5 {
      margin-left: 41.666667%;
    }
    .offset-sm-6 {
      margin-left: 50%;
    }
    .offset-sm-7 {
      margin-left: 58.333333%;
    }
    .offset-sm-8 {
      margin-left: 66.666667%;
    }
    .offset-sm-9 {
      margin-left: 75%;
    }
    .offset-sm-10 {
      margin-left: 83.333333%;
    }
    .offset-sm-11 {
      margin-left: 91.666667%;
    }
  }
  @media (min-width: 768px) {
    .order-md-first {
      order: -1;
    }
    .order-md-last {
      order: 13;
    }
    .order-md-0 {
      order: 0;
    }
    .order-md-1 {
      order: 1;
    }
    .order-md-2 {
      order: 2;
    }
    .order-md-3 {
      order: 3;
    }
    .order-md-4 {
      order: 4;
    }
    .order-md-5 {
      order: 5;
    }
    .order-md-6 {
      order: 6;
    }
    .order-md-7 {
      order: 7;
    }
    .order-md-8 {
      order: 8;
    }
    .order-md-9 {
      order: 9;
    }
    .order-md-10 {
      order: 10;
    }
    .order-md-11 {
      order: 11;
    }
    .order-md-12 {
      order: 12;
    }
    .offset-md-0 {
      margin-left: 0;
    }
    .offset-md-1 {
      margin-left: 8.333333%;
    }
    .offset-md-2 {
      margin-left: 16.666667%;
    }
    .offset-md-3 {
      margin-left: 25%;
    }
    .offset-md-4 {
      margin-left: 33.333333%;
    }
    .offset-md-5 {
      margin-left: 41.666667%;
    }
    .offset-md-6 {
      margin-left: 50%;
    }
    .offset-md-7 {
      margin-left: 58.333333%;
    }
    .offset-md-8 {
      margin-left: 66.666667%;
    }
    .offset-md-9 {
      margin-left: 75%;
    }
    .offset-md-10 {
      margin-left: 83.333333%;
    }
    .offset-md-11 {
      margin-left: 91.666667%;
    }
  }
  @media (min-width: 992px) {
    .order-lg-first {
      order: -1;
    }
    .order-lg-last {
      order: 13;
    }
    .order-lg-0 {
      order: 0;
    }
    .order-lg-1 {
      order: 1;
    }
    .order-lg-2 {
      order: 2;
    }
    .order-lg-3 {
      order: 3;
    }
    .order-lg-4 {
      order: 4;
    }
    .order-lg-5 {
      order: 5;
    }
    .order-lg-6 {
      order: 6;
    }
    .order-lg-7 {
      order: 7;
    }
    .order-lg-8 {
      order: 8;
    }
    .order-lg-9 {
      order: 9;
    }
    .order-lg-10 {
      order: 10;
    }
    .order-lg-11 {
      order: 11;
    }
    .order-lg-12 {
      order: 12;
    }
    .offset-lg-0 {
      margin-left: 0;
    }
    .offset-lg-1 {
      margin-left: 8.333333%;
    }
    .offset-lg-2 {
      margin-left: 16.666667%;
    }
    .offset-lg-3 {
      margin-left: 25%;
    }
    .offset-lg-4 {
      margin-left: 33.333333%;
    }
    .offset-lg-5 {
      margin-left: 41.666667%;
    }
    .offset-lg-6 {
      margin-left: 50%;
    }
    .offset-lg-7 {
      margin-left: 58.333333%;
    }
    .offset-lg-8 {
      margin-left: 66.666667%;
    }
    .offset-lg-9 {
      margin-left: 75%;
    }
    .offset-lg-10 {
      margin-left: 83.333333%;
    }
    .offset-lg-11 {
      margin-left: 91.666667%;
    }
  }
  @media (min-width: 1200px) {
    .order-xl-first {
      order: -1;
    }
    .order-xl-last {
      order: 13;
    }
    .order-xl-0 {
      order: 0;
    }
    .order-xl-1 {
      order: 1;
    }
    .order-xl-2 {
      order: 2;
    }
    .order-xl-3 {
      order: 3;
    }
    .order-xl-4 {
      order: 4;
    }
    .order-xl-5 {
      order: 5;
    }
    .order-xl-6 {
      order: 6;
    }
    .order-xl-7 {
      order: 7;
    }
    .order-xl-8 {
      order: 8;
    }
    .order-xl-9 {
      order: 9;
    }
    .order-xl-10 {
      order: 10;
    }
    .order-xl-11 {
      order: 11;
    }
    .order-xl-12 {
      order: 12;
    }
    .offset-xl-0 {
      margin-left: 0;
    }
    .offset-xl-1 {
      margin-left: 8.333333%;
    }
    .offset-xl-2 {
      margin-left: 16.666667%;
    }
    .offset-xl-3 {
      margin-left: 25%;
    }
    .offset-xl-4 {
      margin-left: 33.333333%;
    }
    .offset-xl-5 {
      margin-left: 41.666667%;
    }
    .offset-xl-6 {
      margin-left: 50%;
    }
    .offset-xl-7 {
      margin-left: 58.333333%;
    }
    .offset-xl-8 {
      margin-left: 66.666667%;
    }
    .offset-xl-9 {
      margin-left: 75%;
    }
    .offset-xl-10 {
      margin-left: 83.333333%;
    }
    .offset-xl-11 {
      margin-left: 91.666667%;
    }
  }

  /* ── Screen reader / visually hidden ─────────────────────────────────── */
  .sr-only {
    position: absolute !important;
    width: 1px !important;
    height: 1px !important;
    padding: 0 !important;
    margin: -1px !important;
    overflow: hidden !important;
    clip: rect(0, 0, 0, 0) !important;
    white-space: nowrap !important;
    border: 0 !important;
  }
  .sr-only-focusable:active,
  .sr-only-focusable:focus {
    position: static !important;
    width: auto !important;
    height: auto !important;
    overflow: visible !important;
    clip: auto !important;
    white-space: normal !important;
  }

  /* ── Media object (Bootstrap 4) ──────────────────────────────────────── */
  .media {
    display: flex;
    align-items: flex-start;
  }
  .media-body {
    flex: 1;
  }

  /* ── Jumbotron ────────────────────────────────────────────────────────── */
  .jumbotron {
    padding: 2rem 1rem;
    margin-bottom: 2rem;
    background-color: ${$.gray200};
    border-radius: ${$.borderRadiusLg};
  }
  @media (min-width: 576px) {
    .jumbotron {
      padding: 4rem 2rem;
    }
  }
  .jumbotron-fluid {
    padding-right: 0;
    padding-left: 0;
    border-radius: 0;
  }
  /* ── Spacing utilities with !important ───────────────────────────────────
     Re-declares Tailwind p-*, m-* etc. with !important so they reliably
     override any non-layered (emotion) CSS setting margin/padding.
     Values match Tailwind default spacing scale (1 unit = 0.25rem). */

  .text-center {
    text-align: center !important;
  }

  /* margin all */
  .m-0 {
    margin: 0 !important;
  }
  .m-1 {
    margin: 0.25rem !important;
  }
  .m-2 {
    margin: 0.5rem !important;
  }
  .m-3 {
    margin: 0.75rem !important;
  }
  .m-4 {
    margin: 1rem !important;
  }
  .m-5 {
    margin: 1.25rem !important;
  }
  .m-6 {
    margin: 1.5rem !important;
  }
  .m-7 {
    margin: 1.75rem !important;
  }
  .m-8 {
    margin: 2rem !important;
  }
  .m-9 {
    margin: 2.25rem !important;
  }
  .m-10 {
    margin: 2.5rem !important;
  }
  .m-12 {
    margin: 3rem !important;
  }
  .m-auto {
    margin: auto !important;
  }
  /* margin x */
  .mx-0 {
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .mx-1 {
    margin-left: 0.25rem !important;
    margin-right: 0.25rem !important;
  }
  .mx-2 {
    margin-left: 0.5rem !important;
    margin-right: 0.5rem !important;
  }
  .mx-3 {
    margin-left: 0.75rem !important;
    margin-right: 0.75rem !important;
  }
  .mx-4 {
    margin-left: 1rem !important;
    margin-right: 1rem !important;
  }
  .mx-5 {
    margin-left: 1.25rem !important;
    margin-right: 1.25rem !important;
  }
  .mx-6 {
    margin-left: 1.5rem !important;
    margin-right: 1.5rem !important;
  }
  .mx-8 {
    margin-left: 2rem !important;
    margin-right: 2rem !important;
  }
  .mx-auto {
    margin-left: auto !important;
    margin-right: auto !important;
  }
  /* margin y */
  .my-0 {
    margin-top: 0 !important;
    margin-bottom: 0 !important;
  }
  .my-1 {
    margin-top: 0.25rem !important;
    margin-bottom: 0.25rem !important;
  }
  .my-2 {
    margin-top: 0.5rem !important;
    margin-bottom: 0.5rem !important;
  }
  .my-3 {
    margin-top: 0.75rem !important;
    margin-bottom: 0.75rem !important;
  }
  .my-4 {
    margin-top: 1rem !important;
    margin-bottom: 1rem !important;
  }
  .my-5 {
    margin-top: 1.25rem !important;
    margin-bottom: 1.25rem !important;
  }
  .my-6 {
    margin-top: 1.5rem !important;
    margin-bottom: 1.5rem !important;
  }
  .my-8 {
    margin-top: 2rem !important;
    margin-bottom: 2rem !important;
  }
  .my-auto {
    margin-top: auto !important;
    margin-bottom: auto !important;
  }
  /* margin top */
  .mt-0 {
    margin-top: 0 !important;
  }
  .mt-1 {
    margin-top: 0.25rem !important;
  }
  .mt-2 {
    margin-top: 0.5rem !important;
  }
  .mt-3 {
    margin-top: 0.75rem !important;
  }
  .mt-4 {
    margin-top: 1rem !important;
  }
  .mt-5 {
    margin-top: 1.25rem !important;
  }
  .mt-6 {
    margin-top: 1.5rem !important;
  }
  .mt-8 {
    margin-top: 2rem !important;
  }
  .mt-10 {
    margin-top: 2.5rem !important;
  }
  .mt-12 {
    margin-top: 3rem !important;
  }
  .mt-auto {
    margin-top: auto !important;
  }
  /* margin bottom */
  .mb-0 {
    margin-bottom: 0 !important;
  }
  .mb-1 {
    margin-bottom: 0.25rem !important;
  }
  .mb-2 {
    margin-bottom: 0.5rem !important;
  }
  .mb-3 {
    margin-bottom: 0.75rem !important;
  }
  .mb-4 {
    margin-bottom: 1rem !important;
  }
  .mb-5 {
    margin-bottom: 1.25rem !important;
  }
  .mb-6 {
    margin-bottom: 1.5rem !important;
  }
  .mb-8 {
    margin-bottom: 2rem !important;
  }
  .mb-10 {
    margin-bottom: 2.5rem !important;
  }
  .mb-12 {
    margin-bottom: 3rem !important;
  }
  .mb-auto {
    margin-bottom: auto !important;
  }
  /* margin left */
  .ml-0 {
    margin-left: 0 !important;
  }
  .ml-1 {
    margin-left: 0.25rem !important;
  }
  .ml-2 {
    margin-left: 0.5rem !important;
  }
  .ml-3 {
    margin-left: 0.75rem !important;
  }
  .ml-4 {
    margin-left: 1rem !important;
  }
  .ml-5 {
    margin-left: 1.25rem !important;
  }
  .ml-6 {
    margin-left: 1.5rem !important;
  }
  .ml-8 {
    margin-left: 2rem !important;
  }
  .ml-auto {
    margin-left: auto !important;
  }
  /* margin right */
  .mr-0 {
    margin-right: 0 !important;
  }
  .mr-1 {
    margin-right: 0.25rem !important;
  }
  .mr-2 {
    margin-right: 0.5rem !important;
  }
  .mr-3 {
    margin-right: 0.75rem !important;
  }
  .mr-4 {
    margin-right: 1rem !important;
  }
  .mr-5 {
    margin-right: 1.25rem !important;
  }
  .mr-6 {
    margin-right: 1.5rem !important;
  }
  .mr-8 {
    margin-right: 2rem !important;
  }
  .mr-auto {
    margin-right: auto !important;
  }
  /* padding all */
  .p-0 {
    padding: 0 !important;
  }
  .p-1 {
    padding: 0.25rem !important;
  }
  .p-2 {
    padding: 0.5rem !important;
  }
  .p-3 {
    padding: 0.75rem !important;
  }
  .p-4 {
    padding: 1rem !important;
  }
  .p-5 {
    padding: 1.25rem !important;
  }
  .p-6 {
    padding: 1.5rem !important;
  }
  .p-7 {
    padding: 1.75rem !important;
  }
  .p-8 {
    padding: 2rem !important;
  }
  .p-10 {
    padding: 2.5rem !important;
  }
  .p-12 {
    padding: 3rem !important;
  }
  /* padding x */
  .px-0 {
    padding-left: 0 !important;
    padding-right: 0 !important;
  }
  .px-1 {
    padding-left: 0.25rem !important;
    padding-right: 0.25rem !important;
  }
  .px-2 {
    padding-left: 0.5rem !important;
    padding-right: 0.5rem !important;
  }
  .px-3 {
    padding-left: 0.75rem !important;
    padding-right: 0.75rem !important;
  }
  .px-4 {
    padding-left: 1rem !important;
    padding-right: 1rem !important;
  }
  .px-5 {
    padding-left: 1.25rem !important;
    padding-right: 1.25rem !important;
  }
  .px-6 {
    padding-left: 1.5rem !important;
    padding-right: 1.5rem !important;
  }
  .px-8 {
    padding-left: 2rem !important;
    padding-right: 2rem !important;
  }
  /* padding y */
  .py-0 {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }
  .py-1 {
    padding-top: 0.25rem !important;
    padding-bottom: 0.25rem !important;
  }
  .py-2 {
    padding-top: 0.5rem !important;
    padding-bottom: 0.5rem !important;
  }
  .py-3 {
    padding-top: 0.75rem !important;
    padding-bottom: 0.75rem !important;
  }
  .py-4 {
    padding-top: 1rem !important;
    padding-bottom: 1rem !important;
  }
  .py-5 {
    padding-top: 1.25rem !important;
    padding-bottom: 1.25rem !important;
  }
  .py-6 {
    padding-top: 1.5rem !important;
    padding-bottom: 1.5rem !important;
  }
  .py-8 {
    padding-top: 2rem !important;
    padding-bottom: 2rem !important;
  }
  /* padding top */
  .pt-0 {
    padding-top: 0 !important;
  }
  .pt-1 {
    padding-top: 0.25rem !important;
  }
  .pt-2 {
    padding-top: 0.5rem !important;
  }
  .pt-3 {
    padding-top: 0.75rem !important;
  }
  .pt-4 {
    padding-top: 1rem !important;
  }
  .pt-5 {
    padding-top: 1.25rem !important;
  }
  .pt-6 {
    padding-top: 1.5rem !important;
  }
  .pt-8 {
    padding-top: 2rem !important;
  }
  .pt-10 {
    padding-top: 2.5rem !important;
  }
  /* padding bottom */
  .pb-0 {
    padding-bottom: 0 !important;
  }
  .pb-1 {
    padding-bottom: 0.25rem !important;
  }
  .pb-2 {
    padding-bottom: 0.5rem !important;
  }
  .pb-3 {
    padding-bottom: 0.75rem !important;
  }
  .pb-4 {
    padding-bottom: 1rem !important;
  }
  .pb-5 {
    padding-bottom: 1.25rem !important;
  }
  .pb-6 {
    padding-bottom: 1.5rem !important;
  }
  .pb-8 {
    padding-bottom: 2rem !important;
  }
  .pb-10 {
    padding-bottom: 2.5rem !important;
  }
  /* padding left */
  .pl-0 {
    padding-left: 0 !important;
  }
  .pl-1 {
    padding-left: 0.25rem !important;
  }
  .pl-2 {
    padding-left: 0.5rem !important;
  }
  .pl-3 {
    padding-left: 0.75rem !important;
  }
  .pl-4 {
    padding-left: 1rem !important;
  }
  .pl-5 {
    padding-left: 1.25rem !important;
  }
  .pl-6 {
    padding-left: 1.5rem !important;
  }
  .pl-8 {
    padding-left: 2rem !important;
  }
  /* padding right */
  .pr-0 {
    padding-right: 0 !important;
  }
  .pr-1 {
    padding-right: 0.25rem !important;
  }
  .pr-2 {
    padding-right: 0.5rem !important;
  }
  .pr-3 {
    padding-right: 0.75rem !important;
  }
  .pr-4 {
    padding-right: 1rem !important;
  }
  .pr-5 {
    padding-right: 1.25rem !important;
  }
  .pr-6 {
    padding-right: 1.5rem !important;
  }
  .pr-8 {
    padding-right: 2rem !important;
  }
  /* gap */
  .gap-0 {
    gap: 0 !important;
  }
  .gap-1 {
    gap: 0.25rem !important;
  }
  .gap-2 {
    gap: 0.5rem !important;
  }
  .gap-3 {
    gap: 0.75rem !important;
  }
  .gap-4 {
    gap: 1rem !important;
  }
  .gap-5 {
    gap: 1.25rem !important;
  }
  .gap-6 {
    gap: 1.5rem !important;
  }
  .gap-8 {
    gap: 2rem !important;
  }

  .jnx-editor-container {
    .input-group {
      margin: 0 !important;
    }
  }

  .alert .text-muted {
    color: inherit !important;
  }

  .flex-important {
    display: flex !important;
  }
`;
